import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { connectSocket, disconnectSocket } from "../utils/socket-client/socket-client";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { createChatHandlers } from "../utils/chat-handlers/chat-handlers";

const ChatContext = createContext(null);
const httpsUrl = process.env.REACT_APP_ENDPOINT_HTTPS;

export const ChatProvider = ({ children }) => {
  const location = useLocation();
  const [socket, setSocket] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [messages, setMessages] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [loadMore, setLoadMore] = useState(0);
  const [remainingMessages, setRemainingMessages] = useState(0);
  const user = useSelector((state) => state.user?.user);
  const [selectedRole, setSelectedRole] = useState("ALL");
  const selectedRoleRef = useRef(selectedRole);
  const [selectedChat, setSelectedChat] = useState(null);
  const receiverIdRef = useRef(null);
  const isSupportPath = location.pathname.includes('support');
  const isSupportPathRef = useRef(isSupportPath);
  const isSuperAdmin = user?.role === 'SUPER-ADMIN';
  const isSuperAdminRef = useRef(isSuperAdmin);
  const handlers = createChatHandlers({
    setChatHistory,
    setMessages,
    setPage,
    setRemainingMessages,
    setHasMore,
    setSelectedChat,
    selectedRoleRef,
    receiverIdRef,
    isSupportPathRef,
    isSuperAdminRef
  });

  useEffect(() => {
    if (!user?.id) return;
    
    const socketInstance = connectSocket("chat", httpsUrl);
    setSocket(socketInstance);

    socketInstance.on("connect", () => {
      socketInstance.emit("get_chat_overview", {
        roleFilter: selectedRoleRef.current,
      });

      if (receiverIdRef.current) {
        socketInstance.emit("get_messages_with_user", {
          receiver_id: receiverIdRef.current,
          page: 1,
          pageSize: 10,
        });
      }

      socketInstance.emit("get_support_overview", {
        roleFilter: isSuperAdminRef.current ? selectedRoleRef.current : 'ALL',
      });

      if (receiverIdRef.current) {
        socketInstance.emit("get_support_conversations_with_user", {
          receiver_id: receiverIdRef.current,
          page: 1,
          pageSize: 10,
        });
      }
    });

    socketInstance.on("chat_overview_response", handlers.handleChatOverview)
    socketInstance.on("messages_with_user_response", handlers.handleMessages);
    socketInstance.on("support_overview_response", handlers.handleSupportOverview);
    socketInstance.on("support_conversations_response", handlers.handleSupportMessages);

    const heartbeat = setInterval(() => {
      socketInstance.emit("heartbeat", { time: Date.now() });
    }, 30000);

    return () => {
      socketInstance.off("chat_overview_response");
      socketInstance.off("messages_with_user_response");
      socketInstance.off("support_overview_response");
      socketInstance.off("support_conversations_response");
      clearInterval(heartbeat);
      disconnectSocket("chat");
    };
  }, [user?.id]);

  useEffect(() => {
    if (!socket) return;
    resetConversation();
    if (isSupportPath) {
      socket.emit("get_support_overview", { 
        roleFilter: isSuperAdminRef.current ? selectedRoleRef.current : 'ALL',
      });
    } else {
      socket.emit("get_chat_overview", { roleFilter: selectedRoleRef.current });
    }
  }, [location.pathname, socket]);

  useEffect(() => {
    isSupportPathRef.current = isSupportPath;
  }, [isSupportPath]);

  useEffect(() => {
    isSuperAdminRef.current = isSuperAdmin;
  }, [isSuperAdmin]);

  useEffect(() => {
    selectedRoleRef.current = selectedRole;
  }, [selectedRole]);

  useEffect(() => {
    if (!socket) return;
    if (selectedChat?.user_id) {
      receiverIdRef.current = selectedChat?.user_id;
      const eventName = isSupportPath ? "get_support_conversations_with_user" : "get_messages_with_user";
      socket.emit(eventName, {
        receiver_id: selectedChat?.user_id,
        page: page + 1,
        pageSize: 10
      });
    }
  }, [socket, selectedChat?.user_id, loadMore]);

  const changeRole = (role) => {
    setSelectedRole(role);
    const eventName = isSupportPath ? "get_support_overview" : "get_chat_overview";
    socket.emit(eventName, {
      roleFilter: role,
    });
  };

  const sendMessage = (payload) => {
    const eventName = isSupportPath ? "send_support_message" : "send_message";
    socket.emit(eventName, payload);
  };

  const resetConversation = () => {
    setMessages(null);
    setPage(0);
    setHasMore(true);
  };

  return (
    <ChatContext.Provider
      value={{
        socket,
        chatHistory,
        setChatHistory,
        messages,
        setMessages,
        hasMore,
        page,
        loadMore,
        setLoadMore,
        resetConversation,
        remainingMessages,
        receiverIdRef,
        selectedRole,
        changeRole,
        selectedChat,
        setSelectedChat,
        sendMessage,
        isSupportPath,
        isSuperAdmin,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => useContext(ChatContext);
